import { Link } from "@tanstack/react-router";
import { MessagesSquare } from "lucide-react";
import { useConversations } from "@/api/queries";
import type { ConversationFilters, ConversationRow } from "@/api/types";
import { StatusBadge } from "@/components/feedback/status-badge";
import { OpportunityLink, OrgLink, StoreLink } from "@/components/navigation/entity-links";
import { AdminTable, type Column } from "@/components/tables/admin-table";
import { type ResourceTableProps, tablePaging } from "@/components/tables/types";
import { formatRelative } from "@/lib/dates";
import { CONVERSATION_AI } from "@/lib/status";

export function ConversationsTable({ query, onChange, toolbar, hide = [], filtered }: ResourceTableProps<ConversationFilters>) {
  const q = useConversations(query);
  const columns: Column<ConversationRow>[] = [
    // {
    //   id: "id",
    //   header: "Conversation",
    //   alwaysVisible: true,
    //   cell: (c) => (
    //     <Link
    //       to="/conversations/$conversationId"
    //       params={{ conversationId: c.id }}
    //       className="font-mono text-[11.5px] hover:underline"
    //       title={c.id}
    //     >
    //       {shortId(c.id)}
    //     </Link>
    //   ),
    // },
    {
      id: "vehicle",
      header: "Vehicle",
      cell: (c) => (
        <Link to="/conversations/$conversationId" params={{ conversationId: c.id }} className=" hover:underline truncate" title={c.vehicle?.title || "-"}>
          {c.vehicle?.title ?? "—"}
        </Link>
      ),
    },
    {
      id: "organization",
      header: "Organization",
      cell: (c) => <OrgLink org={c.organization} />,
    },
    {
      id: "store",
      header: "Store",
      cell: (c) => <StoreLink store={c.store} />,
    },
    { id: "seller", header: "Seller", cell: (c) => c.seller?.name ?? "—" },
    {
      id: "opportunity",
      header: "Opportunity",
      cell: (c) => <OpportunityLink id={c.opportunityId} label="View" />,
    },
    {
      id: "channel",
      header: "Channel",
      defaultHidden: true,
      cell: (c) => c.channel,
    },
    {
      id: "ai",
      header: "AI status",
      cell: (c) => <StatusBadge config={CONVERSATION_AI[c.aiStatus]} />,
    },
    {
      id: "last",
      header: "Last message",
      cell: (c) =>
        c.lastMessage ? (
          <span className="block max-w-72 truncate text-xs">
            <span className="text-muted-foreground">{c.lastMessage.direction === "INBOUND" ? "← " : "→ "}</span>
            {c.lastMessage.body}
          </span>
        ) : (
          "—"
        ),
    },
    {
      id: "activity",
      header: "Last activity",
      sortKey: "lastActivityAt",
      cell: (c) => <span className="text-muted-foreground">{formatRelative(c.lastActivityAt)}</span>,
    },
  ];
  return (
    <AdminTable
      id="conversations"
      label="Conversations"
      resourceName="conversations"
      columns={columns.filter((c) => !hide.includes(c.id))}
      rows={q.data?.items}
      rowKey={(c) => c.id}
      isPending={q.isPending}
      isFetching={q.isFetching}
      error={q.error}
      onRetry={() => void q.refetch()}
      {...tablePaging(query, q.data?.total, onChange)}
      toolbar={toolbar}
      empty={
        filtered
          ? {
              icon: MessagesSquare,
              title: "No conversations match",
              description: "Clear filters to see all conversations.",
            }
          : {
              icon: MessagesSquare,
              title: "No conversations",
              description: "Conversations start when Rogue first texts a seller.",
            }
      }
    />
  );
}
